import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RegistrationSuccess = ({ userEmail, onContinue }) => {
  const [countdown, setCountdown] = useState(5);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (onContinue) {
            onContinue();
          } else {
            navigate('/outage-dashboard');
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, onContinue]);

  const handleContinueNow = () => {
    if (onContinue) {
      onContinue();
    } else {
      navigate('/outage-dashboard');
    }
  };

  return (
    <div className="text-center space-y-6 max-w-md mx-auto">
      {/* Success Icon */}
      <div className="flex items-center justify-center w-20 h-20 bg-success/10 rounded-full mx-auto">
        <Icon name="CheckCircle" size={40} className="text-success" />
      </div>

      {/* Success Message */}
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-foreground">
          Welcome to ServiceWatch!
        </h2>
        <p className="text-muted-foreground">
          Your account has been created successfully. You're now part of our community monitoring service outages worldwide.
        </p>
      </div>

      {/* Email Verification Notice */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full mt-0.5">
            <Icon name="Mail" size={16} className="text-primary" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-foreground mb-1">
              Verify Your Email
            </h3>
            <p className="text-xs text-muted-foreground mb-2">
              We've sent a verification email to:
            </p>
            <p className="text-sm font-medium text-primary break-all">
              {userEmail}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Click the link in the email to verify your account and enable all features.
            </p>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-muted/30 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          What's Next?
        </h3>
        <div className="space-y-2 text-left">
          <div className="flex items-center space-x-2">
            <Icon name="Activity" size={14} className="text-primary" />
            <span className="text-xs text-muted-foreground">
              Explore the live outage dashboard
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="MapPin" size={14} className="text-success" />
            <span className="text-xs text-muted-foreground">
              Set up location-based alerts
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={14} className="text-warning" />
            <span className="text-xs text-muted-foreground">
              Report your first outage
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <Button
          variant="default"
          size="lg"
          fullWidth
          onClick={handleContinueNow}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Continue to Dashboard
        </Button>

        <div className="flex items-center justify-center space-x-4 text-sm">
          <Link
            to="/user-login"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            Sign In Instead
          </Link>
          <span className="text-muted-foreground">•</span>
          <Link
            to="/outage-dashboard"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            Browse Outages
          </Link>
        </div>
      </div>

      {/* Auto-redirect Notice */}
      {countdown > 0 && (
        <div className="text-xs text-muted-foreground">
          Automatically redirecting in {countdown} seconds...
        </div>
      )}
    </div>
  );
};

export default RegistrationSuccess;